import { useState } from 'react';

export type SortDir = 'asc' | 'desc' | null;

/**
 * Сортировка по клику на заголовок таблицы: 1-й клик — по возрастанию, 2-й —
 * по убыванию, 3-й — снимает сортировку. Сразу одной колонкой — выбор новой
 * сбрасывает предыдущую. Общий хук для «Цены» (ClientDetailPage, сортирует
 * уже загруженные строки на клиенте) и списка клиентов (ClientsPage,
 * сортирует на сервере через query-параметр `sort`).
 */
export function useColumnSort<K extends string>(): {
  key: K | null;
  dir: SortDir;
  toggle: (key: K) => void;
} {
  const [key, setKey] = useState<K | null>(null);
  const [dir, setDir] = useState<SortDir>(null);

  const toggle = (nextKey: K) => {
    if (key !== nextKey) {
      setKey(nextKey);
      setDir('asc');
      return;
    }
    if (dir === 'asc') {
      setDir('desc');
      return;
    }
    setKey(null);
    setDir(null);
  };

  return { key, dir, toggle };
}

/** Сортировка уже загруженного (не постранично) набора строк на клиенте. */
export function sortRows<T, K extends keyof T>(rows: T[], key: K | null, dir: SortDir): T[] {
  if (!key || !dir) return rows;
  const copy = [...rows];
  copy.sort((a, b) => {
    const av = a[key];
    const bv = b[key];
    const cmp =
      typeof av === 'number' && typeof bv === 'number'
        ? av - bv
        : String(av ?? '').localeCompare(String(bv ?? ''), 'ru');
    return dir === 'asc' ? cmp : -cmp;
  });
  return copy;
}
